import { formatString } from '@montana/helpers/string-format/string-format.helper';
import {IM08ProductVariant, IProductCode, IProductRequest} from '@montana/models/generated/cms.interface';
import { Dropdown } from '@montana/shared/dropdown';
import { IFieldOptionValue } from '@montana/shared/form/field/field';
import { Link } from '@montana/shared/link';
import { ProductImage } from '@montana/shared/product-image/product-image.component';
import React, { FC, memo, useState, forwardRef, useImperativeHandle, RefObject } from 'react';
import { useCart } from '@montana/containers/cart';
import { useTranslations } from '@montana/containers/translations';
import { getImageUrlFromProductImage } from '@montana/helpers/media-src.helper';
import { IGtmProductWithQuantity } from '@montana/services/tracking/tracking';
import { useTracking } from '@montana/containers/tracking';
import { useSettings } from '@montana/containers/settings/settings.container';

interface IM08ShopTheLookHeroProductProps {
    key: string;
    product: IM08ProductVariant;
    ref?: RefObject<IM08ShopTheLookHeroProductRef> | null;
}

export interface IM08ShopTheLookHeroProductRef {
    getToAddRequest: (originalAmount?: boolean) => IProductRequest;
}

export const M08ShopTheLookHeroProduct: FC<IM08ShopTheLookHeroProductProps> = memo(forwardRef((props: IM08ShopTheLookHeroProductProps, ref): JSX.Element => {
    const { product } = props;
    const [quantity, setQuantity] = useState(product.amount);
    const { addCartLine } = useCart();
    const { trackAddToCart } = useTracking();
    const { market } = useSettings();
    const translate = useTranslations();

    function mapToAddProductModel(originalAmount?: boolean): IProductRequest {
        const productCodes: IProductCode = {
            accessoriesCodes: product.accessoryCodes || [],
            quantity: 1,
            variantCode: product.code || ''
        }
        return {
            code: product.code || '',
            productCodes: [productCodes],
            imageUrl: getImageUrlFromProductImage(product.productImageViewModel),
            productPageQueryString: product.productUrl,
            quantity: originalAmount ? product.amount : quantity,
        };
    }

    function onAddToBasketClick() {
        const productToAdd = mapToAddProductModel();

        if (productToAdd) {

            const gtmProducts: IGtmProductWithQuantity[] = [{
                id: product.code || '',
                name: product.name || '',
                price: product.price?.priceWithDiscount?.amount || 0,
                quantity: product.amount,
                ean: product.price?.ean
            }];

            trackAddToCart(product.price?.priceWithDiscount?.currency || '', gtmProducts);

            addCartLine(productToAdd);
        }
    }

    useImperativeHandle(ref, () => ({
        getToAddRequest: (originalAmount?: boolean) => mapToAddProductModel(originalAmount),
    }));

    function getQuantityOptions() {
        return ['1', '2', '3', '4', '5', '6'].reduce<IFieldOptionValue[]>((prev, val) => {
            prev.push({ value: val, text: val });
            return prev;
        }, []);
    }

    return (
        <article className="shop-the-look-hero-product">
            <Link to={product.productUrl} className="shop-the-look-hero-product__media">
                <ProductImage
                    productImage={product.productImageViewModel}
                    name={product.name}
                    sizes="60px"
                />
            </Link>
            <div className="shop-the-look-hero-product__content">
                <div className="shop-the-look-hero-product__first-col">
                    <Link to={product.productUrl}>
                        <div className="shop-the-look-hero-product__name">{product.name}</div>
                    </Link>
                    {!!product.color && (
                        <div className="shop-the-look-hero-product__color">
                            {formatString(translate(t => t.productPage.shopTheLookHeroProductColor, '[Color — {0}]'), product.color!)}
                        </div>
                    )}
                </div>
                <div className="shop-the-look-hero-product__last-col">
                    {!market?.hidePrices && (
                        <div>
                            <span className="shop-the-look-hero-product__per-piece-label">{translate((t: any) => t.productPage.shopTheLookHeroProductPerUnit, '[pc.]')}</span>
                            <span className="shop-the-look-hero-product__price">{product.price?.priceWithDiscount?.formattedWithCurrency}</span>
                        </div>
                    )}
                    <span className="shop-the-look-hero-product-quantity shop-the-look-hero-product-quantity--spacing">
                        {translate(t => t.productPage.shopTheLookHeroProductQuantity, '[Quantity:]')}
                        {(market?.disableCart || product.price?.visibleNotBuyable) && <span>{quantity}</span>}
                        {(!market?.disableCart && !product.price?.visibleNotBuyable) && (
                            <Dropdown
                                className="shop-the-look-hero-product-quantity__select"
                                options={getQuantityOptions()}
                                value={`${quantity}`}
                                onChange={(val: number) => setQuantity(Number(val))}
                                disabled={market?.disableCart}
                            />
                        )}
                    </span>
                    {!market?.disableCart && !product.price?.visibleNotBuyable && (
                        <button
                            className="shop-the-look-hero-product__add-to-basket"
                            onClick={onAddToBasketClick}
                        >
                            {translate(t => t.productPage.shopTheLookHeroProductAddToBasket, '[Add to basket]')}
                        </button>
                    )}
                </div>
            </div>
        </article>
    );
}));
